import axios from 'axios';
import { useAuthStore } from '@/modules/core/stores/auth.store.ts';
import { useCacheStore } from '@/modules/core/stores/cache.store.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { AuthEndPoint } from '@/modules/common/enums/end-point.ts';
import { shouldEndSessionOnUnauthorized } from '@/modules/core/utils/request-access-token.util.ts';

const instance = axios.create({
  baseURL: 'https://api.alpha-doc.ru/v1.1/',
  headers: { 'Content-Type': 'text/plain' },
});

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (ErrorUtil.isUnauthorized(error) && error.config?.url !== AuthEndPoint.Refresh) {
      const authStore = useAuthStore();

      if (shouldEndSessionOnUnauthorized(authStore.accessToken, error.config?.data)) {
        authStore.handleUnauthorized(true);
      }
    }
    return Promise.reject(error);
  }
);

const MAX_REFRESH_RETRIES = 1;

export type CacheConfig = { group?: string; key: string };
export type ApiRequest = Record<string, unknown> & { where?: never };

// Транспорт не типизирует тело ответа — потребители аннотируют тип на месте вызова
// (const r: Dto = await Api.post(...)). Один осознанный any на границе.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiResponse = any;

export default class Api {
  static RefreshPromise: Promise<void> | null = null;

  static async post(
    url: string,
    request: ApiRequest = {},
    signal?: AbortSignal,
    mapData = true,
    cacheConfig?: CacheConfig,
    retryCount = 0
  ): Promise<ApiResponse> {
    const cacheStore = useCacheStore();

    if (cacheConfig) {
      const cache = cacheStore.get(cacheConfig);

      if (cache) {
        return cache;
      }
    }

    const authStore = useAuthStore();
    const accessToken = authStore.accessToken;

    const modifiedRequest = {
      ...request,
      token: accessToken,
    };

    const response = await instance.post(url, modifiedRequest, { signal });

    const data = response.data;

    if (!data || typeof data.error !== 'boolean') {
      throw {
        error: true,
        error_code: 500,
        error_message: 'Server error',
      };
    }

    if (!data.error) {
      const value = mapData ? data.data : data;

      if (cacheConfig) {
        cacheStore.add({ ...cacheConfig, value });
      }

      return value;
    }

    if (data.error_code !== 401) {
      throw data;
    }

    if (retryCount >= MAX_REFRESH_RETRIES) {
      throw data;
    }

    if (accessToken === authStore.accessToken) {
      await Api.singleRefresh();
    }

    return Api.post(url, request, signal, mapData, cacheConfig, retryCount + 1);
  }

  static beacon(url: string, request: ApiRequest = {}) {
    const body = JSON.stringify({ ...request, token: useAuthStore().accessToken });

    return navigator.sendBeacon(new URL(url, instance.defaults.baseURL).href, new Blob([body], { type: 'text/plain' }));
  }

  static async postRaw(url: string, request: ApiRequest = {}, signal?: AbortSignal) {
    const authStore = useAuthStore();

    const modifiedRequest = {
      ...request,
      token: authStore.accessToken,
    };

    const response = await instance.post(url, modifiedRequest, { signal });

    const data = response.data;

    if (!data || typeof data.error !== 'boolean') {
      throw {
        error: true,
        error_code: 500,
        error_message: 'Server error',
      };
    }

    if (data.error) {
      throw data;
    }

    return data;
  }

  static async singleRefresh() {
    const authStore = useAuthStore();

    if (Api.RefreshPromise) {
      return Api.RefreshPromise;
    }

    Api.RefreshPromise = authStore.refresh();

    try {
      await Api.RefreshPromise;
    } finally {
      Api.RefreshPromise = null;
    }
  }
}
